/* eslint-disable */
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { Form } from 'react-bootstrap'
import ReactQuill from 'react-quill'
import gql from 'graphql-tag'
import { useField } from 'formik'
import styled from 'styled-components'
import { FormGroup } from 'components'
import FullPageLoading from 'components/FullPageLoading'
import { _, withCapture, colors } from 'utils'
import { CreateEventImageMutation, CreateEventImageMutationVariables } from 'generated/graphql'
import graphql from 'utils/graphql'
import { useDebounceValue, useEventSlug } from 'hooks'
import { CommonFieldProps } from '../../types'
import { IMAGE_ONLY } from '../FileUpload'
import * as Modules from './modules'
import { useSelector } from 'react-redux'
import { currentUserSelector } from 'features/auth/authSlice'
import { bindings } from "./modules"

Modules

const S = {
    EditorContainer: styled.div`
        position: relative;
    `,
    FullPageLoading: styled(FullPageLoading)`
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0,0,0,.2);
        padding-bottom: 0;
    `,
    HiddenInput: styled(Form.File)`
        opacity: 0 !important;
        height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden;
    `,
    RawEditor: styled.textarea`
        width: 100%;
        height: 500px;
        border-radius: 4px;
        white-space: pre-wrap;
    `,
    RawButton: styled.div`
        position: absolute;
        top: -20px;
        right: 4px;
        font-weight: 700;
        font-size: 14px;
        color: ${colors.primary};
        cursor: pointer;
    `
}

const createEventImageMutation = gql`
    mutation createEventImage($data: EventImageInput!) {
        createEventImage(data: $data) {
            id
            file
        }
    }
`
const createEventImage = (eventSlug, file) => graphql.createQuery<
  CreateEventImageMutation, CreateEventImageMutationVariables
  >(createEventImageMutation)({ data: { eventSlug, file } })

const Editor = ({
    name,
    label,
    helperText,
    isRequired,
    enableRawView,
    ...props
}: CommonFieldProps & {enableRawView?: boolean}) => {
    const [
        field,
        meta,
        helper
    ] = useField({ name })
    const { error, touched } = meta
    const currentUser = useSelector(currentUserSelector)
    const isSuperUser = currentUser?.is_superuser
    const eventSlug = useEventSlug()
    const editorRef = useRef<ReactQuill>()
    const imageFileInputRef = useRef<HTMLInputElement>()
    const wrapperRef = useRef<HTMLDivElement>()
    const scrollRef = useRef<HTMLDivElement>()
    const [typing, setTyping] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [selection, setSelection] = useState(null)
    const [value, setValue] = useState(field.value)
    const debounceValue = useDebounceValue(value, 400)
    const [rawHtml, setRawHTML] = useState<string>('');
    const [showRaw, setShowRaw] = useState<boolean>(false);
    const handleClickShowRaw = (): void => {
        helper.setTouched(true)
        setShowRaw(!showRaw);
        if (showRaw) {
            setValue(rawHtml)
        } else {
            const convertedData = value?.replace(/<p|<h|<img|<div|<br/gi, function(matched){
                return `\n\n${matched}`;
            })
            setRawHTML(convertedData)
        }
    };

    useEffect(() => {
        if (field.value === undefined && !debounceValue) return
        helper.setValue(debounceValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounceValue])

    useEffect(() => {
        field.value && setValue(field.value)
    }, [field.value])

    const handleUploadImage = useCallback(async (event) => {
        const file = event.target.files[0]
        if (file && imageFileInputRef.current && editorRef.current && selection) {
            setUploading(true)
            const { data: res } = await withCapture(
                async () => await createEventImage(eventSlug, file)
            )()
            const imageUrl = res?.createEventImage?.file
            if (imageUrl) {
                editorRef.current.getEditor().insertEmbed(selection.index, 'image', imageUrl)
                helper.setTouched(true)
            }
            setUploading(false)

            // reset input value after uploaded
            imageFileInputRef.current.value = null
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventSlug, selection])

    const imageHandler = useCallback(() => {
        setSelection(editorRef.current?.getEditor?.().getSelection())
        imageFileInputRef.current?.click?.()
    }, [])

    const modules = useMemo(() => ({
        toolbar: {
            container: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [{ color: [] }, { background: [] }],
                [{ align: [] }],
                [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                ['link', 'image'],
            ],
            handlers: {
                image: imageHandler
            }
        },
        imageResize: {
            modules: ['Resize', 'DisplaySize']
        },
        keyboard: {
            bindings
        },
    }), [imageHandler])

    useEffect(() => {
        // Scroll to bottom when entering the text into the auto-size editor
        typing && scrollRef?.current?.scrollIntoView?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wrapperRef?.current?.clientHeight])

    const handleKeyUp = () => {
        !typing && setTyping(true)
        if (!meta.touched) {
            helper.setTouched(true)
        }
    }

    return (
        <FormGroup {...props}>
            <Form.Label>
                {label}
                {isRequired && <span>&nbsp;*</span>}
            </Form.Label>
            <S.EditorContainer ref={wrapperRef}>
                {isSuperUser && enableRawView && (
                    <S.RawButton onClick={handleClickShowRaw}>
                        {showRaw ? 'Editor view' : 'Raw view'}
                    </S.RawButton>
                )}
                {showRaw ? (
                    <S.RawEditor
                        className={'raw-editor'}
                        onChange={(e): void => setRawHTML(e.target.value)}
                        value={rawHtml}
                    />
                ) : (<ReactQuill
                        value={value || ''}
                        onChange={setValue}
                        modules={modules}
                        ref={editorRef}
                        scrollingContainer="html"
                        onKeyUp={handleKeyUp}
                    />
                )}
                {uploading && <S.FullPageLoading />}
            </S.EditorContainer>
            <div ref={scrollRef} />
            {!(touched && error) && <Form.Text muted>{helperText}</Form.Text>}

            {touched && error && <small className="text-danger invalid-field">{_(error)}</small>}
            <S.HiddenInput
                ref={imageFileInputRef}
                value=""
                accept={IMAGE_ONLY}
                onChange={handleUploadImage}
                custom
            />
        </FormGroup>
    )
}

export default Editor
